import React, { useState } from 'react';
import type { ChatMessage } from '@mem-ai-hub/shared';
import CopyButton from './CopyButton';
import DocumentDownload from './DocumentDownload';
import StreamingText from './StreamingText';
import MarkdownRenderer from './MarkdownRenderer';
import AttachmentChip from './AttachmentChip';

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  streamingContent?: string;
  thinkingContent?: string;
  compact?: boolean;
}

function MessageBubble({
  message,
  isStreaming,
  streamingContent,
  thinkingContent,
  compact = false,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const [thinkingExpanded, setThinkingExpanded] = useState(false);

  const displayContent = isStreaming && streamingContent != null ? streamingContent : message.content;
  const displayThinking = isStreaming ? thinkingContent : message.thinkingContent;

  const timestamp = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%] flex flex-col items-end">
          {/* Attachment chips above user message */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mb-1 flex flex-wrap justify-end gap-1">
              {message.attachments.map((att) => (
                <AttachmentChip
                  key={att.id}
                  fileName={att.fileName}
                  size={att.size}
                  category={att.category}
                />
              ))}
            </div>
          )}
          <div className={`rounded-lg rounded-br-sm bg-primary text-white shadow-sm ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}>
            <p className="whitespace-pre-wrap text-sm">{message.content}</p>
          </div>
          <span className="mt-1 text-xs text-text-muted">{timestamp}</span>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start">
      <div className="group max-w-4xl flex flex-col items-start">
        {/* Thinking section */}
        {displayThinking && (
          <button
            type="button"
            onClick={() => setThinkingExpanded(!thinkingExpanded)}
            className="mb-2 w-full text-left"
          >
            <div className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors">
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <span className="font-medium">Thinking</span>
              <svg
                className={`h-3 w-3 transition-transform ${thinkingExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {thinkingExpanded && (
              <div className="mt-1.5 rounded-md bg-bg-tertiary border border-border-light p-3 text-xs text-text-muted italic whitespace-pre-wrap">
                {displayThinking}
              </div>
            )}
          </button>
        )}

        {/* Message content */}
        <div className={`self-stretch rounded-lg rounded-bl-sm bg-bg-secondary text-text-primary shadow-sm ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}>
          {isStreaming && streamingContent != null ? (
            <StreamingText content={streamingContent} />
          ) : (
            <MarkdownRenderer content={displayContent} />
          )}
        </div>

        {/* Timestamp and action buttons */}
        <div className="mt-1 flex items-center gap-1">
          <span className="text-xs text-text-muted">{timestamp}</span>
          {!isStreaming && (
            <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <CopyButton content={message.content} />
              <DocumentDownload
                messageId={message.id}
                sessionId={message.sessionId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(MessageBubble, (prev, next) => {
  return prev.message.id === next.message.id
    && prev.isStreaming === next.isStreaming
    && prev.streamingContent === next.streamingContent
    && prev.compact === next.compact;
});
